import React from 'react'
import SearchMultiselect from './SearchMultiselect'
import { Country, ICountry } from 'country-state-city'
import { SANCTIONED_COUNTRIES, ZERO_POPULATION_TERRITORIES } from '../util/Constants'
import { generateRoundFlagIcon, generateRoundFlagUrl } from '../util/Flags'

// ---------------------------------------------------------------------------------------------------------------------
// Extend ICountry with a round flag icon
export type ICountryWithFlagIcon = ICountry & {
  flagIconSrc: string
}

// ---------------------------------------------------------------------------------------------------------------------
interface IProps {
  selectedCountry?: string
  disabled?: boolean
  onCountrySelected: (country: ICountryWithFlagIcon | undefined) => void
  // If the allowedCountries argument is missing, the standard list of non-sanctioned countries will be used
  allowedCountries?: ICountry[]
  style?: React.CSSProperties
}

const CountrySelector = (props: IProps): React.ReactElement => {
  // The dropdown label contains the flag icon followed by the country name
  // If the country name is not found, return the supplied country name
  const labelGenerator = (country: string): React.ReactElement => {
    const c = countryByName(country)
    return c ? genLabelWithFlag(c) : <span>{country}</span>
  }

  // Reducer for excluding both sanctioned countries and territories with zero population
  const excludeSanctionedCountries = (acc: ICountryWithFlagIcon[], country: ICountry): ICountryWithFlagIcon[] => {
    if (!SANCTIONED_COUNTRIES.has(country.name) && !ZERO_POPULATION_TERRITORIES.has(country.name)) {
      acc.push({
        ...country,
        flagIconSrc: generateRoundFlagUrl(country.isoCode)
      })
    }
    return acc
  }

  // Reducer for adding flag icon to list of allowed countries
  const extendAllowedCountries = (acc: ICountryWithFlagIcon[], country: ICountry): ICountryWithFlagIcon[] => {
    acc.push({
      ...country,
      flagIconSrc: generateRoundFlagUrl(country.isoCode)
    })

    return acc
  }

  const countries: ICountryWithFlagIcon[] = (props.allowedCountries && props.allowedCountries.length > 0)
    ? props.allowedCountries.reduce(extendAllowedCountries, [])
    : Country.getAllCountries().reduce(excludeSanctionedCountries, [])

  const countryByName = (targetCountry?: string): ICountryWithFlagIcon | undefined =>
    targetCountry ? countries.filter(country => country.name === targetCountry)[0] : undefined

  const genLabelWithFlag = (c: ICountryWithFlagIcon): React.ReactElement => {
    return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
      {generateRoundFlagIcon(c.flagIconSrc)}
      <span>{c.name}</span>
    </span>
  }
  return (
    <SearchMultiselect
      innerStyle={props.style}
      isSingleSelect={true}
      itemArray={countries.map(c => c.name)}
      selectedItems={props.selectedCountry ? [props.selectedCountry] : []}
      setSelectedItems={countries => !!countries[0] && props.onCountrySelected(countryByName(countries[0]))}
      keyExtractor={c => c}
      labelExtractor={labelGenerator}
      placeholder='Select country'
    />)
}

export default CountrySelector
